/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  GeneralMeeting,
  ListGeneralMeetingsResponse,
  ListOrganizationsResponse,
  ListProposalsResponse,
  Organization,
} from '../models';
import {
    GeneralMeetingFromJSON,
    GeneralMeetingToJSON,
    ListGeneralMeetingsResponseFromJSON,
    ListGeneralMeetingsResponseToJSON,
    ListOrganizationsResponseFromJSON,
    ListOrganizationsResponseToJSON,
    ListProposalsResponseFromJSON,
    ListProposalsResponseToJSON,
    OrganizationFromJSON,
    OrganizationToJSON,
} from '../models';

export interface GetGeneralMeetingRequest {
    organizationId: string;
    generalMeetingId: string;
}

export interface GetOrganizationRequest {
    organizationId: string;
}

export interface ListGeneralMeetingsRequest {
    organizationId: string;
    pageSize?: number;
    dateAfter?: Date;
    dateBefore?: Date;
    generalMeetingIds?: Array<string>;
    nextPageToken?: string;
}

export interface ListOrganizationsRequest {
    ids?: Array<string>;
    externalIdentifiers?: Array<string>;
    issuedIsins?: Array<string>;
    pageSize?: number;
    nextPageToken?: string;
}

export interface ListProposalsRequest {
    organizationId: string;
    generalMeetingId: string;
}

/**
 * OrganizationsApi - interface
 * 
 * @export
 * @interface OrganizationsApiInterface
 */
export interface OrganizationsApiInterface {
    /**
     * Returns the general meeting based on its identifier.
     * @summary Get general meeting
     * @param {string} organizationId An identifier of the organization for which information is required.
     * @param {string} generalMeetingId An identifier of the general meeting for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApiInterface
     */
    getGeneralMeetingRaw(requestParameters: GetGeneralMeetingRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<GeneralMeeting>>;

    /**
     * Returns the general meeting based on its identifier.
     * Get general meeting
     */
    getGeneralMeeting(requestParameters: GetGeneralMeetingRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<GeneralMeeting>;

    /**
     * Returns the organization based on its identifier.
     * @summary Get organization
     * @param {string} organizationId An identifier of the organization for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApiInterface
     */
    getOrganizationRaw(requestParameters: GetOrganizationRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Organization>>;

    /**
     * Returns the organization based on its identifier.
     * Get organization
     */
    getOrganization(requestParameters: GetOrganizationRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Organization>;

    /**
     * Lists the general meetings for an organization.  If you wish to list general meetings for any organization, provide a `-` for the organizationId part of the url.   e.g `organizations/-/generalMeetings`  To get a list of general meetings from a list of ids, pass a list of ids in the \'generalMeetingIds\' field and provide a \'-\' for the organizationId part of the url.  General meetings are ordered by ascending date of general meeting. 
     * @summary List general meetings
     * @param {string} organizationId An identifier of the organization for which information is required.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {Date} [dateAfter] Passing a general meeting after date will return general meetings that have a date on or after the specified date.
     * @param {Date} [dateBefore] Passing a general meeting before date will return general meetings that have a date before the specified date.
     * @param {Array<string>} [generalMeetingIds] Passing a list of general meeting ids will return general meetings that match the ids.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApiInterface
     */
    listGeneralMeetingsRaw(requestParameters: ListGeneralMeetingsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListGeneralMeetingsResponse>>;

    /**
     * Lists the general meetings for an organization.  If you wish to list general meetings for any organization, provide a `-` for the organizationId part of the url.   e.g `organizations/-/generalMeetings`  To get a list of general meetings from a list of ids, pass a list of ids in the \'generalMeetingIds\' field and provide a \'-\' for the organizationId part of the url.  General meetings are ordered by ascending date of general meeting. 
     * List general meetings
     */
    listGeneralMeetings(requestParameters: ListGeneralMeetingsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListGeneralMeetingsResponse>;

    /**
     * Returns a list of organizations, including the information available on each one. Without any pagination parameters, a maximum of 50 organizations will be returned.  If information is required for a list of specific organizations, the `issuedIsins`, `externalIdentifiers`, and `ids` query parameters may be used to define a filter for the response such that only organizations matching one defined by the query parameters will be included. For example:      /organizations?issuedIsins=GB00BN7CG237,USG50027AE42  The request returns only organizations for which a match can be found. Any duplicate organizations are returned just once. The order of organizations returned is not determined by the filters.  Note; the filters are additive, so if 2 or more query parameters are provided returned organizations must match both of them. 
     * @summary List organizations
     * @param {Array<string>} [ids] A comma separated list of organization ids.
     * @param {Array<string>} [externalIdentifiers] A comma separated list of organization identifiers.
     * @param {Array<string>} [issuedIsins] A comma separated list of ISINs.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApiInterface
     */
    listOrganizationsRaw(requestParameters: ListOrganizationsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListOrganizationsResponse>>;

    /**
     * Returns a list of organizations, including the information available on each one. Without any pagination parameters, a maximum of 50 organizations will be returned.  If information is required for a list of specific organizations, the `issuedIsins`, `externalIdentifiers`, and `ids` query parameters may be used to define a filter for the response such that only organizations matching one defined by the query parameters will be included. For example:      /organizations?issuedIsins=GB00BN7CG237,USG50027AE42  The request returns only organizations for which a match can be found. Any duplicate organizations are returned just once. The order of organizations returned is not determined by the filters.  Note; the filters are additive, so if 2 or more query parameters are provided returned organizations must match both of them. 
     * List organizations
     */
    listOrganizations(requestParameters: ListOrganizationsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListOrganizationsResponse>;

    /**
     * Returns the proposals for a general meeting.
     * @summary List proposals
     * @param {string} organizationId An identifier of the organization for which information is required.
     * @param {string} generalMeetingId An identifier of the general meeting for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof OrganizationsApiInterface
     */
    listProposalsRaw(requestParameters: ListProposalsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListProposalsResponse>>;

    /**
     * Returns the proposals for a general meeting.
     * List proposals
     */
    listProposals(requestParameters: ListProposalsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListProposalsResponse>;

}

/**
 * 
 */
export class OrganizationsApi extends runtime.BaseAPI implements OrganizationsApiInterface {

    /**
     * Returns the general meeting based on its identifier.
     * Get general meeting
     */
    async getGeneralMeetingRaw(requestParameters: GetGeneralMeetingRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<GeneralMeeting>> {
        if (requestParameters.organizationId === null || requestParameters.organizationId === undefined) {
            throw new runtime.RequiredError('organizationId','Required parameter requestParameters.organizationId was null or undefined when calling getGeneralMeeting.');
        }

        if (requestParameters.generalMeetingId === null || requestParameters.generalMeetingId === undefined) {
            throw new runtime.RequiredError('generalMeetingId','Required parameter requestParameters.generalMeetingId was null or undefined when calling getGeneralMeeting.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/organizations/{organizationId}/generalMeetings/{generalMeetingId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"generalMeetingId"}}`, encodeURIComponent(String(requestParameters.generalMeetingId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => GeneralMeetingFromJSON(jsonValue));
    }

    /**
     * Returns the general meeting based on its identifier.
     * Get general meeting
     */
    async getGeneralMeeting(requestParameters: GetGeneralMeetingRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<GeneralMeeting> {
        const response = await this.getGeneralMeetingRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the organization based on its identifier.
     * Get organization
     */
    async getOrganizationRaw(requestParameters: GetOrganizationRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Organization>> {
        if (requestParameters.organizationId === null || requestParameters.organizationId === undefined) {
            throw new runtime.RequiredError('organizationId','Required parameter requestParameters.organizationId was null or undefined when calling getOrganization.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/organizations/{organizationId}`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => OrganizationFromJSON(jsonValue));
    }

    /**
     * Returns the organization based on its identifier.
     * Get organization
     */
    async getOrganization(requestParameters: GetOrganizationRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Organization> {
        const response = await this.getOrganizationRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Lists the general meetings for an organization.  If you wish to list general meetings for any organization, provide a `-` for the organizationId part of the url.   e.g `organizations/-/generalMeetings`  To get a list of general meetings from a list of ids, pass a list of ids in the \'generalMeetingIds\' field and provide a \'-\' for the organizationId part of the url.  General meetings are ordered by ascending date of general meeting. 
     * List general meetings
     */
    async listGeneralMeetingsRaw(requestParameters: ListGeneralMeetingsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListGeneralMeetingsResponse>> {
        if (requestParameters.organizationId === null || requestParameters.organizationId === undefined) {
            throw new runtime.RequiredError('organizationId','Required parameter requestParameters.organizationId was null or undefined when calling listGeneralMeetings.');
        }

        const queryParameters: any = {};

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.dateAfter !== undefined) {
            queryParameters['dateAfter'] = (requestParameters.dateAfter as any).toISOString().substr(0,10);
        }

        if (requestParameters.dateBefore !== undefined) {
            queryParameters['dateBefore'] = (requestParameters.dateBefore as any).toISOString().substr(0,10);
        }

        if (requestParameters.generalMeetingIds) {
            queryParameters['generalMeetingIds'] = requestParameters.generalMeetingIds;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/organizations/{organizationId}/generalMeetings`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListGeneralMeetingsResponseFromJSON(jsonValue));
    }

    /**
     * Lists the general meetings for an organization.  If you wish to list general meetings for any organization, provide a `-` for the organizationId part of the url.   e.g `organizations/-/generalMeetings`  To get a list of general meetings from a list of ids, pass a list of ids in the \'generalMeetingIds\' field and provide a \'-\' for the organizationId part of the url.  General meetings are ordered by ascending date of general meeting. 
     * List general meetings
     */
    async listGeneralMeetings(requestParameters: ListGeneralMeetingsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListGeneralMeetingsResponse> {
        const response = await this.listGeneralMeetingsRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a list of organizations, including the information available on each one. Without any pagination parameters, a maximum of 50 organizations will be returned.  If information is required for a list of specific organizations, the `issuedIsins`, `externalIdentifiers`, and `ids` query parameters may be used to define a filter for the response such that only organizations matching one defined by the query parameters will be included. For example:      /organizations?issuedIsins=GB00BN7CG237,USG50027AE42  The request returns only organizations for which a match can be found. Any duplicate organizations are returned just once. The order of organizations returned is not determined by the filters.  Note; the filters are additive, so if 2 or more query parameters are provided returned organizations must match both of them. 
     * List organizations
     */
    async listOrganizationsRaw(requestParameters: ListOrganizationsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListOrganizationsResponse>> {
        const queryParameters: any = {};

        if (requestParameters.ids) {
            queryParameters['ids'] = requestParameters.ids.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.externalIdentifiers) {
            queryParameters['externalIdentifiers'] = requestParameters.externalIdentifiers.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.issuedIsins) {
            queryParameters['issuedIsins'] = requestParameters.issuedIsins.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/organizations`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListOrganizationsResponseFromJSON(jsonValue));
    }

    /**
     * Returns a list of organizations, including the information available on each one. Without any pagination parameters, a maximum of 50 organizations will be returned.  If information is required for a list of specific organizations, the `issuedIsins`, `externalIdentifiers`, and `ids` query parameters may be used to define a filter for the response such that only organizations matching one defined by the query parameters will be included. For example:      /organizations?issuedIsins=GB00BN7CG237,USG50027AE42  The request returns only organizations for which a match can be found. Any duplicate organizations are returned just once. The order of organizations returned is not determined by the filters.  Note; the filters are additive, so if 2 or more query parameters are provided returned organizations must match both of them. 
     * List organizations
     */
    async listOrganizations(requestParameters: ListOrganizationsRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListOrganizationsResponse> {
        const response = await this.listOrganizationsRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the proposals for a general meeting.
     * List proposals
     */
    async listProposalsRaw(requestParameters: ListProposalsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListProposalsResponse>> {
        if (requestParameters.organizationId === null || requestParameters.organizationId === undefined) {
            throw new runtime.RequiredError('organizationId','Required parameter requestParameters.organizationId was null or undefined when calling listProposals.');
        }

        if (requestParameters.generalMeetingId === null || requestParameters.generalMeetingId === undefined) {
            throw new runtime.RequiredError('generalMeetingId','Required parameter requestParameters.generalMeetingId was null or undefined when calling listProposals.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/organizations/{organizationId}/generalMeetings/{generalMeetingId}/proposals`.replace(`{${"organizationId"}}`, encodeURIComponent(String(requestParameters.organizationId))).replace(`{${"generalMeetingId"}}`, encodeURIComponent(String(requestParameters.generalMeetingId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListProposalsResponseFromJSON(jsonValue));
    }

    /**
     * Returns the proposals for a general meeting.
     * List proposals
     */
    async listProposals(requestParameters: ListProposalsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListProposalsResponse> {
        const response = await this.listProposalsRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
